import { Alert, AlertTitle, Button, Typography } from "@mui/material";

import { RosterState } from "features/adminUsers/adminUsers";

export interface RosterStateAlertProps {
  /** TaskJob.roster_state; null until the platform has read the file once. */
  roster: RosterState | null;
  canEdit: boolean;
  /** A re-cover request is on its way. */
  recovering: boolean;
  onRecover: () => void;
  /** Why the operator may only look; null when they may act. */
  readOnlyReason?: string | null;
}

function plural(count: number, word: string): string {
  return `${count} ${word}${count === 1 ? "" : "s"}`;
}

/**
 * The roster was rebuilt and its rows no longer match the task ranges.
 *
 * Shown only where the platform will not decide on its own: a file rebuilt
 * while a task was in flight, and one that no longer holds employees the logs
 * have records for. Everything else is re-covered without asking, because
 * re-covering starts no bot - it only changes which task owns which rows.
 */
export default function RosterStateAlert({
  roster,
  canEdit,
  recovering,
  onRecover,
  readOnlyReason,
}: RosterStateAlertProps) {
  if (!roster?.needs_recover) return null;
  const lost = roster.lost_employees ?? 0;
  const blocked = roster.blocked_by_jobs ?? [];

  const action = (
    <Button
      size="small"
      color="inherit"
      variant="outlined"
      disabled={!canEdit || recovering || blocked.length > 0}
      title={
        blocked.length > 0
          ? `Wait for ${blocked.map((id) => `#${id}`).join(", ")} to finish first.`
          : (readOnlyReason ?? undefined)
      }
      onClick={onRecover}
    >
      {recovering ? "Re-covering..." : "Re-cover the roster"}
    </Button>
  );

  return (
    <Alert severity="warning" sx={{ mb: 1 }} action={action}>
      <AlertTitle sx={{ fontWeight: 700 }}>
        {roster.state === "lost"
          ? `The roster no longer holds ${plural(lost, "employee")} that have records.`
          : "The roster was rebuilt and its rows moved."}
      </AlertTitle>
      <Typography variant="body2">
        {roster.state === "lost"
          ? `The stored file now holds ${plural(roster.rows ?? 0, "row")}. Fewer people can mean they left the portal, or that a good file was replaced by a bad one - the two look the same from here, so nothing was changed. Restore the right file, or re-cover the roster to deal this one out across the tasks.`
          : "The task ranges are written in the old row numbers, so they describe other people now. Re-covering deals the whole file out again across the job's tasks."}
      </Typography>
      {blocked.length > 0 ? (
        <Typography variant="body2">
          Job(s) {blocked.map((id) => `#${id}`).join(", ")} still have a task in
          flight - the ranges are left alone until they finish.
        </Typography>
      ) : null}
      <Typography variant="caption" color="text.secondary">
        Re-covering starts no bot: employees that are already done are skipped
        by who they are, so the next run walks only the rest.
      </Typography>
    </Alert>
  );
}
